import { useState, useEffect, useMemo, startTransition } from 'react';
import { isDemoBrand, DEMO_BRAND_ID } from '../utils/constants';
import { fetchWithBrand } from '../utils/api';

/**
 * Fetches all-time monthly campaign data + GA compact summaries on mount.
 * Provides 8+ useMemo derivations for monthly tables, category breakdowns,
 * channel revenue pivot, GA summaries, and weekly views.
 */
export function useMonthlyPerformance(brandId, dateRange, startDate, endDate) {
  const [monthlyGoogleAdsData, setMonthlyGoogleAdsData] = useState(null);
  const [monthlyMetaAdsData, setMonthlyMetaAdsData] = useState(null);
  const [unifiedCampaignData, setUnifiedCampaignData] = useState(null);
  const [unifiedCampaignLast30Data, setUnifiedCampaignLast30Data] = useState(null);
  const [gaMonthlySummaryCompact, setGaMonthlySummaryCompact] = useState(null);
  const [gaWeeklySummaryCompact, setGaWeeklySummaryCompact] = useState(null);
  const [gaDailySummaryCompact, setGaDailySummaryCompact] = useState(null);
  const [weeklyMetric, setWeeklyMetric] = useState('revenue');
  const [isLoading, setIsLoading] = useState(false);

  // ──────────────────────────────────────────────
  // Mount-only fetch: all endpoints with dateRange=all
  // ──────────────────────────────────────────────
  useEffect(() => {
    if (!brandId) return;

    const fetchMonthlyDataCombined = async () => {
      try {
        // Demo: provide 6-month platform-shaped monthly arrays
        if (isDemoBrand(brandId)) {
          const makeMonths = (n = 6) => {
            const out = [];
            const now = new Date();
            for (let i = n - 1; i >= 0; i--) {
              const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
              const ym = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
              out.push(ym);
            }
            return out;
          };
          const months = makeMonths(6);
          const google = months.map((m, idx) => ({
            date: m,
            ga4_sessions: 2500 + idx * 180,
            ga4_total_revenue: 120000 + idx * 9000,
            ga4_purchases: 180 + idx * 10,
            clicks: 3200 + idx * 200,
            cost: 45000 + idx * 2500,
            conversions: 220 + idx * 12,
            conversion_value: 100000 + idx * 8000,
            spend: 0,
            campaign_name: `G-Campaign-${idx + 1}`,
          }));
          const meta = months.map((m, idx) => ({
            date: m,
            ga4_sessions: 1800 + idx * 140,
            ga4_total_revenue: 90000 + idx * 7000,
            ga4_purchases: 130 + idx * 8,
            clicks: 2400 + idx * 160,
            cost: 0,
            conversions: 160 + idx * 9,
            conversion_value: 80000 + idx * 6500,
            spend: 36000 + idx * 2200,
            campaign_name: `M-Campaign-${idx + 1}`,
          }));
          startTransition(() => {
            setMonthlyGoogleAdsData(google);
            setMonthlyMetaAdsData(meta);
          });
          // Demo unified campaign data
          const now = new Date();
          const demoData = [];
          const campaigns = [
            { name: 'Brand Awareness Campaign', channel: 'Google Ads' },
            { name: 'Performance Max - Electronics', channel: 'Google Ads' },
            { name: 'Search - Generic Keywords', channel: 'Google Ads' },
            { name: 'Shopping - Best Sellers', channel: 'Google Ads' },
            { name: 'Meta - Retargeting', channel: 'Meta Ads' },
            { name: 'Meta - Lookalike Audiences', channel: 'Meta Ads' },
            { name: 'Meta - Video Views', channel: 'Meta Ads' },
            { name: 'Instagram - Stories', channel: 'Meta Ads' },
          ];
          for (let i = 0; i < 6; i++) {
            const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 5 + i, 1));
            const monthKey = d.toISOString().split('T')[0];
            campaigns.forEach((camp, cIdx) => {
              const isGoogle = camp.channel === 'Google Ads';
              const baseCost = isGoogle ? 45000 : 32000;
              const baseRevenue = isGoogle ? 185000 : 145000;
              demoData.push({
                month: monthKey,
                campaign_name: camp.name,
                channel: camp.channel,
                ga4_sessions: Math.round(3200 + i * 200 + cIdx * 150),
                ga4_total_revenue: Math.round((baseRevenue + i * 12000 + cIdx * 8000) * (0.9 + Math.random() * 0.2)),
                ga4_purchases: Math.round(250 + i * 20 + cIdx * 10),
                google_clicks: isGoogle ? Math.round(4200 + i * 300 + cIdx * 200) : 0,
                google_cost: isGoogle ? Math.round((baseCost + i * 3000) * (0.9 + Math.random() * 0.2)) : 0,
                google_conversions: isGoogle ? Math.round(280 + i * 15 + cIdx * 8) : 0,
                google_conversion_value: isGoogle ? Math.round((baseRevenue * 0.8 + i * 10000) * (0.9 + Math.random() * 0.2)) : 0,
                meta_clicks: !isGoogle ? Math.round(3100 + i * 250 + cIdx * 180) : 0,
                meta_cost: !isGoogle ? Math.round((baseCost + i * 2500) * (0.9 + Math.random() * 0.2)) : 0,
                meta_conversions: !isGoogle ? Math.round(210 + i * 12 + cIdx * 6) : 0,
                meta_conversion_value: !isGoogle ? Math.round((baseRevenue * 0.75 + i * 9000) * (0.9 + Math.random() * 0.2)) : 0,
              });
            });
          }
          startTransition(() => {
            setUnifiedCampaignData(demoData);
          });
          return;
        }

        // Pull monthly-aggregated ONCE and use for both states (eliminates duplicate API call)
        const monthlyResp = await fetchWithBrand(
          `/api/google-ads/unified-campaign-performance-monthly?dateRange=all`,
          brandId
        );
        const monthlyRows = monthlyResp.ok ? await monthlyResp.json() : [];

        // Transform monthly combined rows into per-platform shapes
        const googleData = [];
        const metaData = [];
        for (const row of monthlyRows) {
          const base = {
            date: row.month,
            ga4_sessions: row.ga4_sessions || 0,
            ga4_total_revenue: row.ga4_total_revenue || 0,
            ga4_purchases: row.ga4_purchases || 0,
            conversions: 0,
            conversion_value: 0,
            clicks: 0,
            cost: 0,
            spend: 0,
          };
          if ((row.channel || '').toLowerCase() === 'google ads') {
            googleData.push({
              ...base,
              clicks: row.google_clicks || 0,
              cost: row.google_cost || 0,
              conversions: row.google_conversions || 0,
              conversion_value: row.google_conversion_value || 0,
              campaign_name: row.campaign_name,
            });
          } else if ((row.channel || '').toLowerCase() === 'meta ads') {
            metaData.push({
              ...base,
              clicks: row.meta_clicks || 0,
              spend: row.meta_cost || 0,
              conversions: row.meta_conversions || 0,
              conversion_value: row.meta_conversion_value || 0,
              campaign_name: row.campaign_name,
            });
          }
        }

        startTransition(() => {
          setMonthlyGoogleAdsData(googleData);
          setMonthlyMetaAdsData(metaData);
          setUnifiedCampaignData(monthlyRows);
        });
      } catch (error) {
        console.error('Error fetching monthly performance data:', error);
      }
    };

    const fetchGaMonthlyCompact = async () => {
      try {
        if (isDemoBrand(brandId)) {
          const now = new Date();
          const demoRows = Array.from({ length: 12 }, (_, i) => {
            const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 11 + i, 1));
            return {
              month: d.toISOString().split('T')[0],
              sessions: 75000 + i * 5000 + Math.random() * 8000,
              users: 52000 + i * 3500 + Math.random() * 5000,
              transactions: 2100 + i * 150 + Math.random() * 200,
              revenue: 1850000 + i * 120000 + Math.random() * 150000,
              bounceRate: 38 + Math.random() * 8,
              avgSessionDuration: 220 + Math.random() * 60,
            };
          });
          setGaMonthlySummaryCompact(demoRows);
          return;
        }
        const res = await fetchWithBrand(`/api/google-ads/ga-summary-monthly?dateRange=all`, brandId);
        if (res.ok) {
          const json = await res.json();
          setGaMonthlySummaryCompact(json?.rows || []);
        }
      } catch (e) {
        console.error('Error fetching GA monthly compact summary:', e);
        setGaMonthlySummaryCompact([]);
      }
    };

    const fetchGaWeeklyCompact = async () => {
      try {
        if (isDemoBrand(brandId)) {
          const now = new Date();
          const demoRows = Array.from({ length: 12 }, (_, i) => {
            const d = new Date(now);
            d.setDate(d.getDate() - (11 - i) * 7);
            return {
              week_start: d.toISOString().split('T')[0],
              sessions: 18500 + i * 800 + Math.random() * 2000,
              users: 13500 + i * 600 + Math.random() * 1500,
              transactions: 520 + i * 30 + Math.random() * 50,
              revenue: 465000 + i * 25000 + Math.random() * 35000,
              bounceRate: 36 + Math.random() * 10,
              avgSessionDuration: 215 + Math.random() * 50,
            };
          });
          setGaWeeklySummaryCompact(demoRows);
          return;
        }
        const res = await fetchWithBrand(`/api/google-ads/ga-summary-weekly?dateRange=all`, brandId);
        if (res.ok) {
          const json = await res.json();
          setGaWeeklySummaryCompact(json?.rows || []);
        }
      } catch (e) {
        console.error('Error fetching GA weekly compact summary:', e);
        setGaWeeklySummaryCompact([]);
      }
    };

    const fetchGaDailyCompact = async () => {
      try {
        if (isDemoBrand(brandId)) {
          const now = new Date();
          const demoRows = Array.from({ length: 30 }, (_, i) => {
            const d = new Date(now);
            d.setDate(d.getDate() - (29 - i));
            const dayOfWeek = d.getDay();
            const weekendFactor = (dayOfWeek === 0 || dayOfWeek === 6) ? 1.15 : 1;
            return {
              date: d.toISOString().split('T')[0],
              sessions: Math.round((2650 + Math.random() * 600) * weekendFactor),
              users: Math.round((1950 + Math.random() * 450) * weekendFactor),
              transactions: Math.round((75 + Math.random() * 20) * weekendFactor),
              revenue: Math.round((66500 + Math.random() * 12000) * weekendFactor),
              bounceRate: 35 + Math.random() * 12,
              avgSessionDuration: 205 + Math.random() * 60,
            };
          });
          setGaDailySummaryCompact(demoRows);
          return;
        }
        const res = await fetchWithBrand(`/api/google-ads/ga-summary-daily?dateRange=all`, brandId);
        if (res.ok) {
          const json = await res.json();
          setGaDailySummaryCompact(json?.rows || []);
        }
      } catch (e) {
        console.error('Error fetching GA daily compact summary:', e);
        setGaDailySummaryCompact([]);
      }
    };

    const fetchLast30 = async () => {
      try {
        if (isDemoBrand(brandId)) return;
        const resp = await fetchWithBrand(
          `/api/google-ads/unified-campaign-performance-last30`,
          brandId
        );
        if (resp.ok) {
          const data = await resp.json();
          setUnifiedCampaignLast30Data(data);
        }
      } catch (e) {
        console.error('Error prefetching last30 unified campaign data:', e);
      }
    };

    setIsLoading(true);
    Promise.allSettled([
      fetchMonthlyDataCombined(),
      fetchGaMonthlyCompact(),
      fetchGaWeeklyCompact(),
      fetchGaDailyCompact(),
      fetchLast30(),
    ]).finally(() => {
      setIsLoading(false);
    });
  }, []); // Mount only

  // ──────────────────────────────────────────────
  // Derived: criteoAdsSummary
  // ──────────────────────────────────────────────
  const criteoAdsSummary = useMemo(() => {
    const empty = { totalClicks: 0, totalSessions: 0, totalConversions: 0, totalGa4Revenue: 0, totalCriteoRevenue: 0, totalCost: 0, cpc: 0, cps: 0, roas: 0 };
    if (!unifiedCampaignData || unifiedCampaignData.length === 0) return empty;

    let criteoRows = unifiedCampaignData.filter(r => (r.channel || '').toLowerCase() === 'criteo');

    if (dateRange !== 'all' && criteoRows.length > 0) {
      const now = new Date();
      let rangeStart, rangeEnd;
      if (dateRange === 'yesterday') {
        rangeStart = new Date(now); rangeStart.setDate(now.getDate() - 1); rangeStart.setHours(0, 0, 0, 0);
        rangeEnd = new Date(rangeStart); rangeEnd.setHours(23, 59, 59, 999);
      } else if (dateRange === 'last7') {
        rangeStart = new Date(now); rangeStart.setDate(now.getDate() - 7); rangeStart.setHours(0, 0, 0, 0);
        rangeEnd = now;
      } else if (dateRange === 'last30') {
        rangeStart = new Date(now); rangeStart.setDate(now.getDate() - 30); rangeStart.setHours(0, 0, 0, 0);
        rangeEnd = now;
      } else if (dateRange === 'last90') {
        rangeStart = new Date(now); rangeStart.setDate(now.getDate() - 90); rangeStart.setHours(0, 0, 0, 0);
        rangeEnd = now;
      } else if (dateRange === 'custom' && startDate && endDate) {
        rangeStart = new Date(startDate);
        rangeEnd = new Date(endDate);
      }
      if (rangeStart && rangeEnd) {
        const rStartMonth = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
        const rEndMonth = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), 1);
        criteoRows = criteoRows.filter(r => {
          const m = new Date(r.month);
          const mMonth = new Date(m.getFullYear(), m.getMonth(), 1);
          return mMonth >= rStartMonth && mMonth <= rEndMonth;
        });
      }
    }

    if (criteoRows.length === 0) return empty;

    const totals = criteoRows.reduce((acc, item) => {
      acc.totalClicks += Number(item.criteo_clicks || 0);
      acc.totalSessions += Number(item.ga4_sessions || 0);
      acc.totalConversions += Number(item.criteo_conversions || 0);
      acc.totalGa4Revenue += Number(item.ga4_total_revenue || 0);
      acc.totalCriteoRevenue += Number(item.criteo_conversion_value || 0);
      acc.totalCost += Number(item.criteo_cost || 0);
      return acc;
    }, { totalClicks: 0, totalSessions: 0, totalConversions: 0, totalGa4Revenue: 0, totalCriteoRevenue: 0, totalCost: 0 });
    const cpc = totals.totalClicks > 0 ? totals.totalCost / totals.totalClicks : 0;
    const cps = totals.totalSessions > 0 ? totals.totalCost / totals.totalSessions : 0;
    const roas = totals.totalCost > 0 ? totals.totalCriteoRevenue / totals.totalCost : 0;
    return { ...totals, cpc, cps, roas };
  }, [unifiedCampaignData, dateRange, startDate, endDate]);

  // ──────────────────────────────────────────────
  // Derived: monthlyPerformanceData
  // ──────────────────────────────────────────────
  const monthlyPerformanceData = useMemo(() => {
    // Extract 'YYYY-MM' without UTC shifting and PRESERVE incoming order
    const getMonthKeyLocal = (dateValue) => {
      if (!dateValue) return '';
      if (typeof dateValue === 'string') {
        const s = dateValue.trim();
        const m = s.match(/^(\d{4})-(\d{2})/);
        if (m) return `${m[1]}-${m[2]}`;
        const d = new Date(s);
        if (isNaN(d)) return '';
        const y = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        return `${y}-${mm}`;
      }
      const d = new Date(dateValue);
      if (isNaN(d)) return '';
      const y = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      return `${y}-${mm}`;
    };
    // Format month label, shifting display by +1 month per backend convention
    const formatMonthLabelPlusOne = (monthKey) => {
      if (!monthKey || !/^\d{4}-\d{2}$/.test(monthKey)) return '';
      const [yy, mm] = monthKey.split('-').map(Number);
      const date = new Date(yy, (mm - 1) + 1, 1); // +1 month for display
      return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    const processPlatformData = (data) => {
      if (!Array.isArray(data) || data.length === 0) return null;

      const monthOrder = [];
      const monthlyTotals = data.reduce((acc, item) => {
        const month = getMonthKeyLocal(item.date);
        if (!month) return acc;
        if (!acc[month]) {
          acc[month] = { cost: 0, clicks: 0, conversions: 0, revenue: 0, ga4_sessions: 0, ga4_total_revenue: 0, ga4_purchases: 0 };
          monthOrder.push(month);
        }
        acc[month].cost += (item.cost || item.spend || 0);
        acc[month].clicks += item.clicks || 0;
        acc[month].conversions += item.conversions || 0;
        acc[month].revenue += item.conversion_value || 0;
        acc[month].ga4_sessions += item.ga4_sessions || 0;
        acc[month].ga4_total_revenue += item.ga4_total_revenue || 0;
        acc[month].ga4_purchases += item.ga4_purchases || 0;
        return acc;
      }, {});

      const orderFinal = monthOrder.slice();
      const platformTotals = orderFinal.reduce((acc, key) => {
        const m = monthlyTotals[key] || {};
        acc.cost += m.cost || 0;
        acc.clicks += m.clicks || 0;
        acc.conversions += m.conversions || 0;
        acc.revenue += m.revenue || 0;
        acc.ga4_sessions += m.ga4_sessions || 0;
        acc.ga4_total_revenue += m.ga4_total_revenue || 0;
        acc.ga4_purchases += m.ga4_purchases || 0;
        return acc;
      }, { cost: 0, clicks: 0, conversions: 0, revenue: 0, ga4_sessions: 0, ga4_total_revenue: 0, ga4_purchases: 0 });

      const monthsData = orderFinal.map((monthStr, index) => {
        const monthData = monthlyTotals[monthStr];
        const prevMonthStr = orderFinal[index + 1];
        const prevMonthData = prevMonthStr ? monthlyTotals[prevMonthStr] : null;

        const roas = monthData.cost > 0 ? monthData.revenue / monthData.cost : 0;
        let roasChange = null;
        if (prevMonthData) {
          const prevRoas = prevMonthData.cost > 0 ? prevMonthData.revenue / prevMonthData.cost : 0;
          if (prevRoas > 0) {
            roasChange = ((roas - prevRoas) / prevRoas) * 100;
          }
        }

        // Compute display month key (shifted +1) for USD rate lookup
        const [rawY, rawM] = monthStr.split('-').map(Number);
        const shiftedDate = new Date(rawY, rawM, 1);
        const displayMonthKey = `${shiftedDate.getFullYear()}-${String(shiftedDate.getMonth() + 1).padStart(2, '0')}`;

        return {
          month: formatMonthLabelPlusOne(monthStr),
          displayMonthKey,
          cost: monthData.cost,
          clicks: monthData.clicks,
          conversions: monthData.conversions,
          revenue: monthData.revenue,
          ga4_sessions: monthData.ga4_sessions,
          ga4_total_revenue: monthData.ga4_total_revenue,
          ga4_purchases: monthData.ga4_purchases,
          cpc: monthData.clicks > 0 ? monthData.cost / monthData.clicks : 0,
          cps: monthData.ga4_sessions > 0 ? monthData.cost / monthData.ga4_sessions : 0,
          cpa: monthData.conversions > 0 ? monthData.cost / monthData.conversions : 0,
          cr: monthData.clicks > 0 ? (monthData.conversions / monthData.clicks) * 100 : 0,
          ga4_roas: monthData.cost > 0 ? monthData.ga4_total_revenue / monthData.cost : 0,
          roas: roas,
          roasChange: roasChange,
        };
      });

      return {
        totals: {
          ...platformTotals,
          cpc: platformTotals.clicks > 0 ? platformTotals.cost / platformTotals.clicks : 0,
          cps: platformTotals.ga4_sessions > 0 ? platformTotals.cost / platformTotals.ga4_sessions : 0,
          cpa: platformTotals.conversions > 0 ? platformTotals.cost / platformTotals.conversions : 0,
          cr: platformTotals.clicks > 0 ? (platformTotals.conversions / platformTotals.clicks) * 100 : 0,
          ga4_roas: platformTotals.cost > 0 ? platformTotals.ga4_total_revenue / platformTotals.cost : 0,
          roas: platformTotals.cost > 0 ? platformTotals.revenue / platformTotals.cost : 0,
        },
        months: monthsData,
      };
    };

    const googleData = processPlatformData(monthlyGoogleAdsData);
    const metaData = processPlatformData(monthlyMetaAdsData);

    if (!googleData && !metaData) return null;

    const overallTotal = {
      cost: (googleData?.totals.cost || 0) + (metaData?.totals.cost || 0),
      clicks: (googleData?.totals.clicks || 0) + (metaData?.totals.clicks || 0),
      conversions: (googleData?.totals.conversions || 0) + (metaData?.totals.conversions || 0),
      revenue: (googleData?.totals.revenue || 0) + (metaData?.totals.revenue || 0),
      ga4_sessions: (googleData?.totals.ga4_sessions || 0) + (metaData?.totals.ga4_sessions || 0),
      ga4_total_revenue: (googleData?.totals.ga4_total_revenue || 0) + (metaData?.totals.ga4_total_revenue || 0),
      ga4_purchases: (googleData?.totals.ga4_purchases || 0) + (metaData?.totals.ga4_purchases || 0),
    };

    overallTotal.cpc = overallTotal.clicks > 0 ? overallTotal.cost / overallTotal.clicks : 0;
    overallTotal.cps = overallTotal.ga4_sessions > 0 ? overallTotal.cost / overallTotal.ga4_sessions : 0;
    overallTotal.cpa = overallTotal.conversions > 0 ? overallTotal.cost / overallTotal.conversions : 0;
    overallTotal.cr = overallTotal.clicks > 0 ? (overallTotal.conversions / overallTotal.clicks) * 100 : 0;
    overallTotal.ga4_roas = overallTotal.cost > 0 ? overallTotal.ga4_total_revenue / overallTotal.cost : 0;
    overallTotal.roas = overallTotal.cost > 0 ? overallTotal.revenue / overallTotal.cost : 0;

    return {
      'Google Ads': googleData,
      'Meta Ads': metaData,
      'Total': overallTotal,
    };
  }, [monthlyGoogleAdsData, monthlyMetaAdsData]);

  // ──────────────────────────────────────────────
  // Derived: allCostsByMonth
  // ──────────────────────────────────────────────
  const allCostsByMonth = useMemo(() => {
    if (!monthlyPerformanceData) return {};
    const costsByMonth = {};
    const googleMonths = monthlyPerformanceData['Google Ads']?.months || [];
    const metaMonths = monthlyPerformanceData['Meta Ads']?.months || [];
    const addPlatformMonths = (monthsArr) => {
      monthsArr.forEach((m) => {
        const key = m.month;
        const cost = Number(m.cost || 0);
        costsByMonth[key] = (costsByMonth[key] || 0) + cost;
      });
    };
    addPlatformMonths(googleMonths);
    addPlatformMonths(metaMonths);
    return costsByMonth;
  }, [monthlyPerformanceData]);

  // ──────────────────────────────────────────────
  // Derived: totalAllCosts
  // ──────────────────────────────────────────────
  const totalAllCosts = useMemo(() => {
    return Number(monthlyPerformanceData?.Total?.cost || 0);
  }, [monthlyPerformanceData]);

  // ──────────────────────────────────────────────
  // Derived: monthlyPlatformCategoryData (Performance vs Branding)
  // ──────────────────────────────────────────────
  const monthlyPlatformCategoryData = useMemo(() => {
    const getMonthKeyLocal = (dateValue) => {
      if (!dateValue) return '';
      if (typeof dateValue === 'string') {
        const s = dateValue.trim();
        const m = s.match(/^(\d{4})-(\d{2})/);
        if (m) return `${m[1]}-${m[2]}`;
        const d = new Date(s);
        if (isNaN(d)) return '';
        const y = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        return `${y}-${mm}`;
      }
      const d = new Date(dateValue);
      if (isNaN(d)) return '';
      const y = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      return `${y}-${mm}`;
    };
    const formatMonthLabelPlusOne = (monthKey) => {
      if (!monthKey || !/^\d{4}-\d{2}$/.test(monthKey)) return '';
      const [yy, mm] = monthKey.split('-').map(Number);
      const date = new Date(yy, (mm - 1) + 1, 1);
      return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    const buildCategoryStruct = (rows) => {
      if (!Array.isArray(rows) || rows.length === 0) return null;
      const buckets = { Performance: {}, Branding: {} };
      const orderPerf = [];
      const orderBrand = [];
      const toCategory = (item) => {
        const name = (item.campaign_name || '').toString().toLowerCase();
        const objective = (item.campaign_objective || '').toString().toLowerCase();
        // Name-based patterns
        const perfNameHit = name.includes('performance') || name.includes('performans') || name.includes('perf');
        const brandNameHit = name.includes('branding') || name.includes('brand') || name.includes('awareness');
        if (perfNameHit) return 'Performance';
        if (brandNameHit) return 'Branding';
        // Objective-based fallback
        const perfObjHit = ['sales', 'conversion', 'conversions', 'leads', 'traffic', 'purchase'].some(k => objective.includes(k));
        const brandObjHit = ['awareness', 'reach', 'brand'].some(k => objective.includes(k));
        if (perfObjHit) return 'Performance';
        if (brandObjHit) return 'Branding';
        return null;
      };

      for (const item of rows) {
        const cat = toCategory(item);
        if (!cat) continue;
        const monthKey = getMonthKeyLocal(item.date);
        const bucket = buckets[cat];
        if (!bucket[monthKey]) {
          bucket[monthKey] = { cost: 0, clicks: 0, conversions: 0, revenue: 0, ga4_sessions: 0, ga4_total_revenue: 0, ga4_purchases: 0 };
          if (cat === 'Performance') orderPerf.push(monthKey);
          else orderBrand.push(monthKey);
        }
        const m = bucket[monthKey];
        m.cost += (item.cost || item.spend || 0);
        m.clicks += item.clicks || 0;
        m.conversions += item.conversions || 0;
        m.revenue += item.conversion_value || 0;
        m.ga4_sessions += item.ga4_sessions || 0;
        m.ga4_total_revenue += item.ga4_total_revenue || 0;
        m.ga4_purchases += item.ga4_purchases || 0;
      }

      const buildStruct = (bucket, months) => {
        const totals = { cost: 0, clicks: 0, conversions: 0, revenue: 0, ga4_sessions: 0, ga4_total_revenue: 0, ga4_purchases: 0 };
        const rowsOut = months.map((monthStr, idx) => {
          const v = bucket[monthStr];
          totals.cost += v.cost;
          totals.clicks += v.clicks;
          totals.conversions += v.conversions;
          totals.revenue += v.revenue;
          totals.ga4_sessions += v.ga4_sessions;
          totals.ga4_total_revenue += v.ga4_total_revenue;
          totals.ga4_purchases += v.ga4_purchases;

          const prevMonthStr = months[idx + 1];
          const prev = prevMonthStr ? bucket[prevMonthStr] : null;
          const roas = v.cost > 0 ? v.revenue / v.cost : 0;
          let roasChange = null;
          if (prev) {
            const prevRoas = prev.cost > 0 ? prev.revenue / prev.cost : 0;
            if (prevRoas > 0) roasChange = ((roas - prevRoas) / prevRoas) * 100;
          }

          const [rawY2, rawM2] = monthStr.split('-').map(Number);
          const shiftedDate2 = new Date(rawY2, rawM2, 1);
          const displayMonthKey = `${shiftedDate2.getFullYear()}-${String(shiftedDate2.getMonth() + 1).padStart(2, '0')}`;

          return {
            month: formatMonthLabelPlusOne(monthStr),
            displayMonthKey,
            cost: v.cost,
            clicks: v.clicks,
            conversions: v.conversions,
            revenue: v.revenue,
            ga4_sessions: v.ga4_sessions,
            ga4_total_revenue: v.ga4_total_revenue,
            ga4_purchases: v.ga4_purchases,
            cpc: v.clicks > 0 ? v.cost / v.clicks : 0,
            cps: v.ga4_sessions > 0 ? v.cost / v.ga4_sessions : 0,
            cpa: v.conversions > 0 ? v.cost / v.conversions : 0,
            cr: v.clicks > 0 ? (v.conversions / v.clicks) * 100 : 0,
            ga4_roas: v.cost > 0 ? v.ga4_total_revenue / v.cost : 0,
            roas,
            roasChange,
          };
        });

        const totalsEnriched = {
          ...totals,
          cpc: totals.clicks > 0 ? totals.cost / totals.clicks : 0,
          cps: totals.ga4_sessions > 0 ? totals.cost / totals.ga4_sessions : 0,
          cpa: totals.conversions > 0 ? totals.cost / totals.conversions : 0,
          cr: totals.clicks > 0 ? (totals.conversions / totals.clicks) * 100 : 0,
          ga4_roas: totals.cost > 0 ? totals.ga4_total_revenue / totals.cost : 0,
          roas: totals.cost > 0 ? totals.revenue / totals.cost : 0,
        };
        return { totals: totalsEnriched, months: rowsOut };
      };

      const perf = buildStruct(buckets.Performance, orderPerf);
      const brand = buildStruct(buckets.Branding, orderBrand);
      const total = {
        cost: (perf?.totals.cost || 0) + (brand?.totals.cost || 0),
        clicks: (perf?.totals.clicks || 0) + (brand?.totals.clicks || 0),
        conversions: (perf?.totals.conversions || 0) + (brand?.totals.conversions || 0),
        revenue: (perf?.totals.revenue || 0) + (brand?.totals.revenue || 0),
        ga4_sessions: (perf?.totals.ga4_sessions || 0) + (brand?.totals.ga4_sessions || 0),
        ga4_total_revenue: (perf?.totals.ga4_total_revenue || 0) + (brand?.totals.ga4_total_revenue || 0),
        ga4_purchases: (perf?.totals.ga4_purchases || 0) + (brand?.totals.ga4_purchases || 0),
      };
      total.cpc = total.clicks > 0 ? total.cost / total.clicks : 0;
      total.cps = total.ga4_sessions > 0 ? total.cost / total.ga4_sessions : 0;
      total.cpa = total.conversions > 0 ? total.cost / total.conversions : 0;
      total.cr = total.clicks > 0 ? (total.conversions / total.clicks) * 100 : 0;
      total.ga4_roas = total.cost > 0 ? total.ga4_total_revenue / total.cost : 0;
      total.roas = total.cost > 0 ? total.revenue / total.cost : 0;

      return { Performance: perf, Branding: brand, Total: total };
    };

    const googleStruct = buildCategoryStruct(monthlyGoogleAdsData || []);
    const metaStruct = buildCategoryStruct(monthlyMetaAdsData || []);
    if (!googleStruct && !metaStruct) return null;

    const overall = {
      cost: (googleStruct?.Total.cost || 0) + (metaStruct?.Total.cost || 0),
      clicks: (googleStruct?.Total.clicks || 0) + (metaStruct?.Total.clicks || 0),
      conversions: (googleStruct?.Total.conversions || 0) + (metaStruct?.Total.conversions || 0),
      revenue: (googleStruct?.Total.revenue || 0) + (metaStruct?.Total.revenue || 0),
      ga4_sessions: (googleStruct?.Total.ga4_sessions || 0) + (metaStruct?.Total.ga4_sessions || 0),
      ga4_total_revenue: (googleStruct?.Total.ga4_total_revenue || 0) + (metaStruct?.Total.ga4_total_revenue || 0),
      ga4_purchases: (googleStruct?.Total.ga4_purchases || 0) + (metaStruct?.Total.ga4_purchases || 0),
    };
    overall.cpc = overall.clicks > 0 ? overall.cost / overall.clicks : 0;
    overall.cps = overall.ga4_sessions > 0 ? overall.cost / overall.ga4_sessions : 0;
    overall.cpa = overall.conversions > 0 ? overall.cost / overall.conversions : 0;
    overall.cr = overall.clicks > 0 ? (overall.conversions / overall.clicks) * 100 : 0;
    overall.ga4_roas = overall.cost > 0 ? overall.ga4_total_revenue / overall.cost : 0;
    overall.roas = overall.cost > 0 ? overall.revenue / overall.cost : 0;

    return {
      'Google Ads': googleStruct,
      'Meta Ads': metaStruct,
      'Total': overall,
    };
  }, [monthlyGoogleAdsData, monthlyMetaAdsData]);

  // ──────────────────────────────────────────────
  // Derived: channelMonthlyRevenue
  // ──────────────────────────────────────────────
  const channelMonthlyRevenue = useMemo(() => {
    if (!unifiedCampaignData) return null;

    const channels = [...new Set(unifiedCampaignData.map(item => item.channel || 'Unknown'))];
    const initialChannelData = channels.reduce((acc, channel) => {
      acc[channel] = { revenue: 0, sessions: 0, transactions: 0, cost: 0 };
      return acc;
    }, {});

    const pivotData = unifiedCampaignData.reduce((acc, item) => {
      const dateOrMonth = item.month || item.date;
      const month = new Date(dateOrMonth).toLocaleString('default', { month: 'long', year: 'numeric' });
      const channel = item.channel || 'Unknown';
      const revenue = Number(item.ga4_total_revenue || 0);
      const sessions = Number(item.ga4_sessions || 0);
      const transactions = Number(item.ga4_purchases || 0);
      const googleCost = Number(item.google_cost || 0);
      const metaCost = Number(item.meta_cost || 0);
      const criteoCost = Number(item.criteo_cost || 0);
      const totalCost = googleCost + metaCost + criteoCost;

      if (!acc[month]) {
        acc[month] = {
          ...JSON.parse(JSON.stringify(initialChannelData)),
          Total: { revenue: 0, sessions: 0, transactions: 0, cost: 0 },
        };
      }

      if (acc[month][channel]) {
        acc[month][channel].revenue += revenue;
        acc[month][channel].sessions += sessions;
        acc[month][channel].transactions += transactions;
      }

      if (channel === 'Google Ads') acc[month][channel].cost += googleCost;
      if (channel === 'Meta Ads') acc[month][channel].cost += metaCost;
      if (channel === 'Criteo') acc[month][channel].cost += criteoCost;

      acc[month].Total.revenue += revenue;
      acc[month].Total.sessions += sessions;
      acc[month].Total.transactions += transactions;
      acc[month].Total.cost += totalCost;

      return acc;
    }, {});

    const sortedMonths = Object.keys(pivotData).sort((a, b) => new Date(b) - new Date(a));

    const tableData = sortedMonths.map(month => ({
      month,
      ...pivotData[month],
    }));

    const initialTotals = { Total: { revenue: 0, sessions: 0, transactions: 0, cost: 0 } };
    channels.forEach(ch => initialTotals[ch] = { revenue: 0, sessions: 0, transactions: 0, cost: 0 });

    const totals = tableData.reduce((acc, row) => {
      channels.forEach(channel => {
        acc[channel].revenue += row[channel].revenue;
        acc[channel].sessions += row[channel].sessions;
        acc[channel].transactions += row[channel].transactions;
        acc[channel].cost += row[channel].cost;
      });
      acc.Total.revenue += row.Total.revenue;
      acc.Total.sessions += row.Total.sessions;
      acc.Total.transactions += row.Total.transactions;
      acc.Total.cost += row.Total.cost;
      return acc;
    }, initialTotals);

    return {
      headers: ['Month', ...channels, 'Total'],
      rows: tableData,
      totals: totals,
    };
  }, [unifiedCampaignData]);

  // ──────────────────────────────────────────────
  // Derived: gaMonthlySummary
  // ──────────────────────────────────────────────
  const gaMonthlySummary = useMemo(() => {
    if (!Array.isArray(gaMonthlySummaryCompact)) return null;
    const rows = [...gaMonthlySummaryCompact];
    rows.sort((a, b) => new Date(b.month) - new Date(a.month));
    const monthlyData = rows.map(r => {
      const conversionRate = (Number(r.sessions || 0) > 0)
        ? (Number(r.transactions || 0) / Number(r.sessions || 0)) * 100
        : 0;
      const d = new Date(r.month);
      const displayMonthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      return {
        month: d.toLocaleString('default', { month: 'long', year: 'numeric' }),
        displayMonthKey,
        sessions: Number(r.sessions || 0),
        users: Number(r.users || 0),
        totalRevenue: Number(r.revenue || 0),
        eventCount: Number(r.transactions || 0),
        conversionRate,
      };
    });
    const totals = monthlyData.reduce((acc, m) => {
      acc.sessions += m.sessions;
      acc.users += m.users;
      acc.eventCount += m.eventCount;
      acc.totalRevenue += m.totalRevenue;
      return acc;
    }, { sessions: 0, users: 0, eventCount: 0, totalRevenue: 0 });
    totals.conversionRate = totals.sessions > 0 ? (totals.eventCount / totals.sessions) * 100 : 0;
    return { monthlyData, totals };
  }, [gaMonthlySummaryCompact]);

  // ──────────────────────────────────────────────
  // Derived: weeklyRevenueByWeekday
  // ──────────────────────────────────────────────
  const weeklyRevenueByWeekday = useMemo(() => {
    if (!Array.isArray(gaDailySummaryCompact)) return null;
    const pivotMap = new Map();
    for (const r of gaDailySummaryCompact) {
      const weekKey = r.week_start || r.date;
      if (!pivotMap.has(weekKey)) {
        pivotMap.set(weekKey, {
          isoWeek: Number(r.iso_week || 0),
          rev: Array(7).fill(0),
          sess: Array(7).fill(0),
          trans: Array(7).fill(0),
          dates: Array(7).fill(null),
        });
      }
      const agg = pivotMap.get(weekKey);
      const idx = Math.max(0, Math.min(6, (Number(r.iso_dow || 1) - 1)));
      agg.rev[idx] += Number(r.revenue || 0);
      agg.sess[idx] += Number(r.sessions || 0);
      agg.trans[idx] += Number(r.transactions || 0);
      agg.dates[idx] = r.date;
    }
    const rows = Array.from(pivotMap.entries())
      .sort((a, b) => new Date(b[0]) - new Date(a[0]))
      .slice(0, 10)
      .map(([weekStart, agg]) => {
        let days = [];
        let total = 0;
        if (weeklyMetric === 'revenue') {
          days = agg.rev;
          total = agg.rev.reduce((s, v) => s + (Number(v) || 0), 0);
        } else if (weeklyMetric === 'sessions') {
          days = agg.sess;
          total = agg.sess.reduce((s, v) => s + (Number(v) || 0), 0);
        } else if (weeklyMetric === 'transactions') {
          days = agg.trans;
          total = agg.trans.reduce((s, v) => s + (Number(v) || 0), 0);
        } else {
          days = agg.sess.map((sVal, idx) => sVal > 0 ? (agg.trans[idx] / sVal) * 100 : 0);
          const totalSess = agg.sess.reduce((s, v) => s + (Number(v) || 0), 0);
          const totalTrans = agg.trans.reduce((s, v) => s + (Number(v) || 0), 0);
          total = totalSess > 0 ? (totalTrans / totalSess) * 100 : 0;
        }
        return {
          weekKey: `W${String(agg.isoWeek || '')}`,
          days,
          dates: agg.dates,
          total,
        };
      });
    return rows;
  }, [gaDailySummaryCompact, weeklyMetric]);

  // ──────────────────────────────────────────────
  // Derived: weeklyWoWSummary
  // ──────────────────────────────────────────────
  const weeklyWoWSummary = useMemo(() => {
    if (!Array.isArray(gaWeeklySummaryCompact)) return null;
    const arr = [...gaWeeklySummaryCompact].sort((a, b) => new Date(b.week) - new Date(a.week));
    const rows = arr.map((r, idx, a) => {
      const revenue = Number(r.revenue || 0);
      const sessions = Number(r.sessions || 0);
      const transactions = Number(r.transactions || 0);
      const cr = sessions > 0 ? (transactions / sessions) * 100 : 0;
      const prev = a[idx + 1];
      const pct = (cur, prevVal) => {
        if (!prevVal || prevVal === 0) return 0;
        return ((cur - prevVal) / prevVal) * 100;
      };
      const deltas = prev ? {
        rev: pct(revenue, Number(prev.revenue || 0)),
        trans: pct(transactions, Number(prev.transactions || 0)),
        sess: pct(sessions, Number(prev.sessions || 0)),
        cr: pct(cr, (Number(prev.sessions || 0) > 0 ? (Number(prev.transactions || 0) / Number(prev.sessions || 0)) * 100 : 0)),
      } : { rev: 0, trans: 0, sess: 0, cr: 0 };
      const dateObj = new Date(r.week);
      const getISOWeek = (d) => {
        const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        const dayNum = dt.getUTCDay() || 7;
        dt.setUTCDate(dt.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
        return Math.ceil((((dt - yearStart) / 86400000) + 1) / 7);
      };
      const weekKey = `W${String(getISOWeek(dateObj)).padStart(2, '0')}`;
      return { weekKey, revenue, sessions, transactions, cr, deltas };
    });
    return rows.slice(0, 10);
  }, [gaWeeklySummaryCompact]);

  // ──────────────────────────────────────────────
  // Return
  // ──────────────────────────────────────────────
  return {
    monthlyPerformanceData,
    monthlyPlatformCategoryData,
    criteoAdsSummary,
    channelMonthlyRevenue,
    allCostsByMonth,
    totalAllCosts,
    gaMonthlySummary,
    weeklyRevenueByWeekday,
    weeklyWoWSummary,
    weeklyMetric,
    setWeeklyMetric,
    unifiedCampaignData,
    unifiedCampaignLast30Data,
    isLoading,
  };
}
